import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiGrid, FiBook, FiFileText, FiMonitor, FiBarChart2, FiSidebar, FiLogOut, FiShield, FiUserCheck, FiUsers } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';



const EXPANDED_WIDTH = 250;
const COLLAPSED_WIDTH = 70;

const AdminSideNav = ({ isOpen, collapsed, onToggleCollapse }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [logoHovered, setLogoHovered] = useState(false);
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const navItems = [
        { to: user?.role === 'superadmin' ? '/superadmin/dashboard' : '/admin/dashboard', icon: <FiGrid />, label: 'Home' },
        ...(user?.role === 'superadmin' ? [
            { to: '/superadmin/manage-admins', icon: <FiShield />, label: 'Admins' },
            { to: '/superadmin/manage-teachers', icon: <FiUsers />, label: 'Teachers' },
            { to: '/superadmin/review-papers', icon: <FiFileText />, label: 'Review Papers' },
            { to: '/superadmin/students', icon: <FiUsers />, label: 'Students' }
        ] : [
            { to: '/admin/review-papers', icon: <FiFileText />, label: 'Review Papers' }
        ]),
        // { to: '/admin/subjects', icon: <FiBook />, label: 'Subjects' },
        { to: '/admin/exams', icon: <FiFileText />, label: 'Exams' },
        { to: '/admin/teacher-assignments', icon: <FiUsers />, label: 'Assign Teachers' },
        { to: '/admin/monitor', icon: <FiMonitor />, label: 'Monitor' },
        { to: '/admin/results', icon: <FiBarChart2 />, label: 'Results' },
    ];

    if (isMobile) {
        return (
            <nav style={{
                position: 'fixed', bottom: 0, left: 0, right: 0, height: 64,
                background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)', borderTop: '1px solid rgba(0,0,0,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-around',
                zIndex: 1000, boxShadow: '0 -4px 20px rgba(0,0,0,0.06)',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}>
                {navItems.map(item => (
                    <NavLink key={item.to} to={item.to} className="mobile-nav-hover"
                        style={({ isActive }) => ({
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                            textDecoration: 'none', color: isActive ? '#059669' : '#94a3b8',
                            fontSize: 10, fontWeight: isActive ? 700 : 500, fontFamily: 'Outfit, sans-serif',
                            padding: '6px 10px', borderRadius: 12,
                            background: isActive ? 'rgba(5,150,105,0.08)' : 'transparent',
                            transition: 'all 0.2s ease',
                        })}
                    >
                        <span style={{ fontSize: 20, lineHeight: 1 }}>{item.icon}</span>
                        <span>{item.label}</span>
                    </NavLink>
                ))}
            </nav>
        );
    }

    const currentWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

    return (
        <aside style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, width: currentWidth,
            background: 'linear-gradient(180deg, #022c22 0%, #064e3b 100%)',
            display: 'flex', flexDirection: 'column', zIndex: 999,
            boxShadow: '4px 0 20px rgba(0,0,0,0.15)',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
        }}>
            {/* Header with toggle */}
            <div style={{
                padding: collapsed ? '16px 10px' : '16px 20px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                transition: 'padding 0.3s ease',
            }}>
                {collapsed ? (
                    <div
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, position: 'relative' }}
                        onMouseEnter={() => setLogoHovered(true)}
                        onMouseLeave={() => setLogoHovered(false)}
                    >
                        <div
                            onClick={onToggleCollapse}
                            style={{
                                width: 42, height: 42, borderRadius: 12,
                                background: 'linear-gradient(135deg, #059669, #10b981)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 20, cursor: 'pointer', position: 'relative',
                                transition: 'all 0.25s ease',
                            }}
                        >
                            <span style={{
                                opacity: logoHovered ? 0 : 1,
                                transition: 'opacity 0.2s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}><img src="/svu-logo.png" alt="SVU" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} /></span>
                            <span style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                opacity: logoHovered ? 1 : 0,
                                transition: 'opacity 0.2s ease',
                                color: 'white',
                            }}>
                                <FiSidebar size={16} />
                            </span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 12,
                        }}>
                            <div style={{
                                width: 42, height: 42, borderRadius: 12,
                                background: 'linear-gradient(135deg, #059669, #10b981)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 20, flexShrink: 0,
                            }}><img src="/svu-logo.png" alt="SVU" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} /></div>
                            <button
                                onClick={onToggleCollapse}
                                title="Collapse sidebar"
                                className="sidebar-item-hover"
                                style={{
                                    width: 30, height: 30, borderRadius: 8, border: 'none',
                                    background: 'transparent', color: 'rgba(255,255,255,0.45)',
                                    cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    padding: 0, transition: 'all 0.2s ease',
                                }}
                            >
                                <FiSidebar size={16} />
                            </button>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <h2 style={{ color: 'white', fontSize: 14, fontWeight: 700, fontFamily: 'Outfit, sans-serif', margin: 0, lineHeight: 1.3 }}>
                                Swami Vivekananda<br />University
                            </h2>
                            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, margin: '4px 0 0', fontFamily: 'Outfit, sans-serif' }}>
                                {user?.role === 'superadmin' ? 'Super Admin Portal' : 'Admin Portal'}
                            </p>
                        </div>
                    </>
                )}
            </div>
            <nav style={{ flex: 1, padding: collapsed ? '16px 8px' : '16px 12px', overflowY: 'auto', transition: 'padding 0.3s ease' }}>
                {!collapsed && (
                    <div style={{
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5,
                        color: 'rgba(255,255,255,0.35)', padding: '8px 12px 6px', fontFamily: 'Outfit, sans-serif',
                    }}>Management</div>
                )}
                {navItems.map(item => (
                    <NavLink key={item.to} to={item.to} className="sidebar-item-hover"
                        title={collapsed ? item.label : ''}
                        style={({ isActive }) => ({
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: collapsed ? '10px 0' : '10px 14px',
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            borderRadius: 10, textDecoration: 'none', fontSize: 14, fontWeight: 500, marginBottom: 2,
                            fontFamily: 'Outfit, sans-serif', transition: 'all 0.2s ease',
                            color: isActive ? 'white' : 'rgba(255,255,255,0.6)',
                            background: isActive ? 'rgba(16,185,129,0.2)' : 'transparent',
                        })}
                    >
                        <span style={{ fontSize: collapsed ? 20 : 16, display: 'flex', minWidth: collapsed ? 'auto' : 16 }}>{item.icon}</span>
                        {!collapsed && <span>{item.label}</span>}
                    </NavLink>
                ))}
            </nav>

            <div style={{ padding: collapsed ? '8px' : '8px 12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <button
                    onClick={handleLogout}
                    className="sidebar-item-hover"
                    title={collapsed ? 'Logout' : ''}
                    style={{
                        width: '100%', display: 'flex', alignItems: 'center',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        gap: 10, padding: collapsed ? '10px 0' : '10px 14px',
                        borderRadius: 10, border: 'none',
                        background: 'rgba(239,68,68,0.1)', color: '#f87171',
                        cursor: 'pointer', fontSize: 14, fontWeight: 500,
                        fontFamily: 'Outfit, sans-serif', transition: 'all 0.2s ease',
                    }}
                >
                    <span style={{ fontSize: collapsed ? 20 : 16, display: 'flex' }}><FiLogOut /></span>
                    {!collapsed && <span>Logout</span>}
                </button>
            </div>

            {!collapsed && (
                <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>SVU Exam Portal v1.0</p>
                </div>
            )}
        </aside>
    );
};

export default AdminSideNav;
